import { useMemo } from "react";
import { TransactionListItem } from "@common/schemas/transaction.schema";
import { capitalize } from "@common/helpers/string";
import { TransactionColumn } from "@common/types/views/transactionViews";
import { CATEGORY_COLUMN_SIZE } from "@design-system/ViewTable/DefaultColumnSizes";
import { ViewConfig } from "@lib/views";
import { useTransactionViewConfig } from "../../transactions/views/useTransactionViewConfig";
import { TransactionSortValue } from "../../transactions/useSort";
import { BulkUpdateParams } from "../../transactions/TransactionsBulkActions/lib";
import {
    getPropertyLabel,
    getTransactionColumnIdForUpdate,
    NEW_VALUE_COLUMN_ID,
} from "./SimilarTransactionsUpdateDrawer.lib";
import { NewValueCell } from "./NewValueCell";

type SimilarTransactionsViewConfig = ViewConfig<
    TransactionListItem,
    string,
    TransactionSortValue
>;

const COMPACT_CATEGORY_COLUMN_SIZE = {
    minSize: 200,
    maxSize: 230,
};

function getCompactUpdateColumnSize(update: BulkUpdateParams["update"]) {
    return update === "categoryId" ? COMPACT_CATEGORY_COLUMN_SIZE : undefined;
}

export function useSimilarTransactionsViewConfig(
    valueToAssign: BulkUpdateParams,
): SimilarTransactionsViewConfig {
    const baseConfig = useTransactionViewConfig();
    const propertyLabel = getPropertyLabel(valueToAssign.update);

    return useMemo(() => {
        const columns: SimilarTransactionsViewConfig["columns"] = {};
        const columnToRename = getTransactionColumnIdForUpdate(
            valueToAssign.update,
        );
        const compactUpdateColumnSize = getCompactUpdateColumnSize(
            valueToAssign.update,
        );

        for (const [id, config] of Object.entries(baseConfig.columns)) {
            if (columnToRename && id === columnToRename) {
                columns[id] = {
                    ...config,
                    header: capitalize(propertyLabel),
                    name: capitalize(propertyLabel),
                    ...compactUpdateColumnSize,
                };
            } else if (id === TransactionColumn.ENTITY) {
                // Wider than the default 62px to prevent entity icon truncation
                // in the drawer's narrower layout.
                columns[id] = {
                    ...config,
                    minSize: 72,
                    maxSize: 72,
                };
            } else {
                columns[id] = config;
            }
        }

        columns[NEW_VALUE_COLUMN_ID] = {
            accessorKey: "id",
            name: `New ${capitalize(propertyLabel)}`,
            header: `New ${capitalize(propertyLabel)}`,
            cell: NewValueCell,
            immutable: true,
            hiddenFromSettings: true,
            ...(compactUpdateColumnSize ?? CATEGORY_COLUMN_SIZE),
        };

        return {
            ...baseConfig,
            columns,
        };
    }, [baseConfig, valueToAssign.update, propertyLabel]);
}
